// Single upload path for user-attached files (bug screenshots, support ticket
// attachments). R2 is the sole storage backend; public buckets are served from
// cdn.podgo.io.

import { uploadToR2, r2PublicUrl, type R2Bucket } from "@/services/r2Upload";

/** Upload one file and return its public cdn URL. */
export async function uploadAttachment(
  bucket: R2Bucket,
  file: Blob,
  fileName: string,
): Promise<string> {
  const safeName = (fileName || "file").replace(/[^a-zA-Z0-9._-]/g, "_");
  const path = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}-${safeName}`;
  await uploadToR2({
    bucket,
    path,
    file,
    contentType: file.type || "application/octet-stream",
  });
  return r2PublicUrl(bucket, path);
}

/** Upload every file in order, returning the resulting public URLs. */
export async function uploadAttachments(
  bucket: R2Bucket,
  files: File[],
): Promise<string[]> {
  const urls: string[] = [];
  for (const file of files) {
    urls.push(await uploadAttachment(bucket, file, file.name));
  }
  return urls;
}

const IMAGE_EXT = /\.(png|jpe?g|gif|webp|avif|bmp|svg)(\?|#|$)/i;

/** URL points at an image we can render inline. */
export function isImageUrl(url: string): boolean {
  return IMAGE_EXT.test(url);
}

/** Last path segment, stripped of the upload timestamp prefix. */
export function attachmentFileName(url: string): string {
  try {
    const raw = decodeURIComponent(new URL(url).pathname.split("/").pop() || url);
    return raw.replace(/^\d{10,}-[a-z0-9]{2,8}-/i, "").replace(/^\d{10,}-/, "");
  } catch {
    return url;
  }
}
